import { ref } from 'vue'
import { fetchAllTeamsWithMembers } from '@/services/auth'
import type { TeamWithMembers } from '@/types/auth'

/**
 * Singleton de módulo (mesmo padrão de useAdmin.ts/useSLA.ts). "Listar times +
 * team_members de cada" é uma varredura cara (1 request por time, em lotes) que
 * várias telas precisam pro mesmo dado — login e ProfileView pra saber os
 * próprios times do usuário; DashboardView, AvaliacoesView, AgentesView,
 * LastAccessSection e AttentionSection pra montar o mapa agente→times. Sem
 * cache, cada tela repetia a varredura inteira (visto em produção: chega a
 * disparar de novo a cada navegação, multiplicando as requisições contra o
 * Chatwoot self-hosted). Cacheia por (accountId, token) pela duração da sessão
 * — `force: true` ignora o cache.
 */
const teamsWithMembers = ref<TeamWithMembers[]>([])
let loadedKey: string | null = null
let inFlight: Promise<TeamWithMembers[]> | null = null

// Fase 4.2 do plano de otimização de login (docs/planos/PLANO-OTIMIZACAO-LOGIN.md):
// chamado no logout — sem isso, um agente B logando na mesma aba depois de A
// veria a membership de A por um instante (só se corrigia sozinho quando um
// load() com chave nova terminasse). Limpa o cache e cancela o loadedKey pra
// forçar um fetch novo, mesmo que o próximo load() venha com a mesma chave
// accountId:token de antes (ex.: mesmo agente logando de novo).
export function resetTeamsWithMembers(): void {
  teamsWithMembers.value = []
  loadedKey = null
  inFlight = null
}

export function useTeamsWithMembers() {
  async function load(
    accountId: number,
    token: string,
    opts: { force?: boolean } = {},
  ): Promise<TeamWithMembers[]> {
    const key = `${accountId}:${token}`
    if (opts.force) loadedKey = null
    if (inFlight && loadedKey === key) return inFlight
    if (loadedKey === key) return teamsWithMembers.value

    loadedKey = key
    inFlight = fetchAllTeamsWithMembers(accountId, token)
      .then(teams => { teamsWithMembers.value = teams; return teams })
      .finally(() => { inFlight = null })
    return inFlight
  }

  return { teamsWithMembers, load }
}
